import useMenuRedirect from 'containers/Menu/hooks/useMenuRedirect';
import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';
import React, { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectHeadbandState } from 'selectors/headband';
import getFirstNode from 'utils/getFirstNode';

const VCARD_PATH_PATTERN = /^\/[^/]+\/vcard\//;

export const useGetContentStyles = () => {
  const contentDivRef = useRef(null);
  const footerRef = useRef(null);
  const { isMobile } = useVerifyIsMobile(768);
  const { nodesSelected } = useMenuRedirect();
  const { pathname } = useLocation();
  const firstNode = getFirstNode();
  const { isVisible: isHeadBandVisible } = useSelector(selectHeadbandState);
  const { showNotification, hasNotification, showCdnInNode } = useSelector(
    state => state?.cvNotifications,
  );

  const isVCardPath = VCARD_PATH_PATTERN.test(pathname);

  const updateFooterPosition = useCallback(() => {
    const contentDiv = contentDivRef.current;
    const footer = footerRef.current;
    if (!contentDiv || !footer) return;

    if (isVCardPath) {
      footer.classList.remove('fixed-footer');
      return;
    }

    const scrollHeight = contentDiv.scrollHeight;
    const clientHeight = contentDiv.clientHeight;
    const contentHasScroll = isMobile
      ? scrollHeight >= clientHeight
      : scrollHeight > clientHeight;

    if (contentHasScroll) {
      footer.classList.remove('fixed-footer');
    } else {
      footer.classList.add('fixed-footer');
    }
  }, [isMobile, isVCardPath]);

  const MENU_HEIGHTS = {
    MOBILE: 60, // < 918px
    TABLET: 150, // 918px - 1250px
    DESKTOP: 120, // > 1250px
  };

  const NOTIFICATION_COLLAPSED_HEIGHTS = {
    MOBILE: 99, // < 918px
    DESKTOP: 60, // >= 918px
  };

  // ---obtener la altura del centro de notificaciones dinmicamente---
  const getNotificationHeight = () => {
    const notificationElement = document.getElementById('CDN-notifications');

    if (!notificationElement) return 0;

    const computedStyle = window.getComputedStyle(notificationElement);
    //---margen---
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
    //---Height---
    const height = notificationElement.offsetHeight;
    //--- Altura total---
    const totalHeight = height + marginTop + marginBottom;

    return totalHeight;
  };

  // ---obtener la altura del menu según resolucion ---
  const getMenuHeight = width => {
    if (width <= 918) return MENU_HEIGHTS.MOBILE;
    if (width <= 1250) return MENU_HEIGHTS.TABLET;
    return MENU_HEIGHTS.DESKTOP;
  };

  // --- Settear estilos al menu con notificaciones ---
  const applyHeaderStyles = useCallback(
    () => {
      if (!contentDivRef.current) return;
      //Se toman los de las hojas de estilos
      if (isHeadBandVisible || !showCdnInNode || nodesSelected?.code !== firstNode) {
        contentDivRef.current.style.marginTop = '';
        contentDivRef.current.style.height = '';
        return;
      }

      const width = window.innerWidth;
      const menuHeight = getMenuHeight(width);

      let marginTop = menuHeight;
      let height = '';
      let notificationTotalHeight = 0;

      // si hasNotification es true se muestra CDN Replegado
      if (hasNotification) {
        if (showNotification) {
          // ---desplegado centro de notificaciones con show notification
          notificationTotalHeight = getNotificationHeight();

          // ---Si no pudimos obtener la altura ponemos fallback---
          if (notificationTotalHeight === 0) {
            notificationTotalHeight =
              width <= 918
                ? NOTIFICATION_COLLAPSED_HEIGHTS.MOBILE
                : NOTIFICATION_COLLAPSED_HEIGHTS.DESKTOP;
          }
        } else {
          // ---Estado replegado: usamos altura fija---
          notificationTotalHeight =
            width <= 918
              ? NOTIFICATION_COLLAPSED_HEIGHTS.MOBILE
              : NOTIFICATION_COLLAPSED_HEIGHTS.DESKTOP;
        }

        marginTop = menuHeight + notificationTotalHeight;
      }

      // ---Calcular height----
      height = hasNotification
        ? `calc(var(--vh, 100vh) - ${menuHeight + notificationTotalHeight}px)`
        : `calc(var(--vh, 100vh) - ${menuHeight}px)`;

      // ----Aplicar estilos----
      contentDivRef.current.style.marginTop = `${marginTop}px`;
      contentDivRef.current.style.height = height;
    },
    [hasNotification, showNotification, showCdnInNode, nodesSelected],
  );

  useLayoutEffect(
    () => {
      const footer = footerRef?.current;
      const contentDiv = contentDivRef?.current;
      if (!footer || !contentDiv) return;

      if (footer.classList.contains('display-footer'))
        footer.classList.remove('display-footer');

      const timeoutId = setTimeout(() => {
        footer.classList.add('display-footer');

        if (footer.classList.contains('footer_web--hidden'))
          contentDiv.classList.remove('content_div--with-footer');
        else contentDiv.classList.add('content_div--with-footer');

        updateFooterPosition();
      }, 2000);

      return () => clearTimeout(timeoutId);
    },
    [pathname, updateFooterPosition],
  );

  useEffect(
    () => {
      const contentDiv = contentDivRef.current;
      const footer = footerRef.current;
      if (!contentDiv || !footer) return;

      updateFooterPosition();

      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateFooterPosition);
      });
      resizeObserver.observe(contentDiv);

      const mutationObserver = new MutationObserver(() => {
        requestAnimationFrame(updateFooterPosition);
      });
      mutationObserver.observe(contentDiv, {
        childList: true,
        subtree: true,
      });

      window.addEventListener('load', updateFooterPosition);

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        window.removeEventListener('load', updateFooterPosition);
      };
    },
    [updateFooterPosition, pathname],
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    const setViewportHeight = () => {
      const vh = Number(window.innerHeight);
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // On foldable devices (e.g. Z Fold) the browser may fire `resize` before
    // innerHeight reflects the new orientation. A double-RAF guarantees we
    // read the updated value after layout is complete.
    const setViewportHeightAfterLayout = () => {
      requestAnimationFrame(() => requestAnimationFrame(setViewportHeight));
    };

    setViewportHeight();

    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('load', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeightAfterLayout);

    return () => {
      document.body.style.overflow = 'visible';
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('load', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeightAfterLayout);
    };
  }, []);

  useEffect(
    () => {
      //--- Aplicar estilos inicialmente---
      applyHeaderStyles();

      const handleResize = () => {
        applyHeaderStyles();
      };

      //---observar cambios en CDN
      window.addEventListener('resize', handleResize);
      const notificationObserver = new MutationObserver(() => {
        applyHeaderStyles();
      });
      const notificationElement = document.getElementById('CDN-notifications');
      if (notificationElement) {
        notificationObserver.observe(notificationElement, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }

      // Observar cambios en la clase del contentDiv
      const contentObserver = new MutationObserver(applyHeaderStyles);

      if (contentDivRef.current) {
        contentObserver.observe(contentDivRef.current, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        notificationObserver.disconnect();
        contentObserver.disconnect();
      };
    },
    [applyHeaderStyles, hasNotification, showNotification, showCdnInNode, nodesSelected],
  );

  return {
    contentDivRef,
    footerRef,
  };
};